import clsx from 'clsx';

import ForecastIcon from '@/components/ForecastIcon';
import { QuarteredForecast } from '@/models/QuarteredForecast';
import ArrowIcon from '@/public/assets/ui-icons/arrow.svg';
import { AppConfig } from '@/utils/AppConfig';
import { getLocaleDate, isDateToday, isDateTomorrow, isWeekendDate } from '@/utils/DateUtils';

type CommonDayInfoProps = Pick<
  QuarteredForecast,
  'dayOfWeek' | 'date' | 'temperatureHigh' | 'temperatureLow' | 'icon' | 'description'
> & {
  open?: boolean;
};

const CommonDayInfo = ({
  dayOfWeek,
  date,
  temperatureHigh,
  temperatureLow,
  icon,
  description,
  open,
}: CommonDayInfoProps) => {
  const localeDate = getLocaleDate(date);
  const isWeekend = isWeekendDate(date);

  let dayOfWeekString = dayOfWeek;
  if (isDateToday(date, AppConfig.serverDate)) {
    dayOfWeekString = 'Сегодня';
  } else if (isDateTomorrow(date, AppConfig.serverDate)) {
    dayOfWeekString = 'Завтра';
  }

  return (
    <div className="flex justify-between xl:items-baseline">
      <div className="flex-1 text-sm text-grey-800 lg:ml-2.5 lg:flex-auto lg:text-lg xl:text-xl">
        <h3
          className={clsx(
            'block text-lg font-medium lg:text-xl xl:inline xl:text-2xl',
            isWeekend ? 'text-red' : 'text-grey-900'
          )}
        >
          {dayOfWeekString}
          <span className="hidden xl:inline">,</span>
        </h3>{' '}
        {localeDate}
      </div>
      <div className="flex shrink-0">
        <div className="mr-4 hidden max-w-[240px] pt-3.5 text-right text-lg font-medium leading-tight lg:block xl:max-w-[380px] xl:pt-5 xl:text-xl xl:leading-tight">
          {description}
        </div>
        <ForecastIcon
          iconName={icon}
          className="mr-2 w-10 shrink-0 self-center lg:mr-5 lg:h-[70px] lg:w-[70px] xl:h-20 xl:w-20"
          alt={description}
        />
        <div className="flex items-baseline self-start pt-1.5 lg:pt-2.5 xl:pt-3">
          <div className="min-w-[46px] text-xl font-medium lg:min-w-[56px] lg:text-2xl xl:min-w-[68px] xl:text-3xl">
            {temperatureHigh}&deg;
          </div>
          <div className="ml-0.5 min-w-[36px] text-base font-medium text-blue-700 lg:min-w-[46px] lg:text-xl xl:min-w-[56px] xl:text-2xl">
            {temperatureLow}&deg;
          </div>
        </div>
      </div>
      <div
        role="button"
        aria-label="Details"
        className="pt-4 text-blue-grey-500 sm:flex-1 lg:ml-9 lg:flex-initial lg:pt-[18px] lg:group-hover:text-blue-500 xl:ml-14 xl:pt-5"
      >
        <ArrowIcon
          className={clsx('h-[11px] w-5 shrink-0 sm:ml-auto lg:mr-[18px] lg:ml-0 lg:h-5 lg:w-[37px] xl:mr-6', {
            'rotate-180': open,
          })}
        />
      </div>
    </div>
  );
};

export default CommonDayInfo;
